import { ACHIEVEMENT_STARS, type AchievementDto } from '@spin/shared';
import { useEffect, useState } from 'react';
import { http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Spinner } from '../components/common';
import { CloseIcon } from '../components/icons';
import { reportError, useT } from '../i18n';

/** The stars of an achievement on an arc above its medal. */
function StarArc({ total, earned }: { total: number; earned: number }) {
  return (
    <span className="star-arc" aria-label={`${earned} / ${total}`}>
      {Array.from({ length: total }, (_, i) => {
        const offset = i - (total - 1) / 2;
        return (
          <i
            key={i}
            className={i < earned ? 'on' : ''}
            style={{ translate: `0 ${Math.abs(offset) * 3}px`, rotate: `${offset * 12}deg` }}
          >
            ★
          </i>
        );
      })}
    </span>
  );
}

function Medal({ a, size = 'normal' }: { a: AchievementDto; size?: 'normal' | 'large' }) {
  return (
    <span className={`medal ${a.stars ? 'earned' : 'locked'} ${size}`}>
      <StarArc total={a.levels.length} earned={a.stars} />
      <span className="medal-disc">
        <span className="medal-icon">{a.icon}</span>
      </span>
    </span>
  );
}

/** "My achievements ★ N/total": medals with star levels; completed or locked ones first. */
export function AchievementsDialog() {
  const t = useT();
  const [items, setItems] = useState<AchievementDto[] | null>(null);
  const [first, setFirst] = useState<'completed' | 'locked'>('completed');
  const [open, setOpen] = useState<AchievementDto | null>(null);

  useEffect(() => {
    http.get<AchievementDto[]>('/achievements').then(setItems).catch(reportError);
  }, []);

  const stars = items?.reduce((sum, a) => sum + a.stars, 0) ?? 0;
  const shown = [...(items ?? [])].sort((a, b) =>
    first === 'completed' ? b.stars - a.stars : a.stars - b.stars,
  );
  const goal = open ? (open.levels[open.stars] ?? open.levels.at(-1)!) : 0;

  return (
    <Dialog
      className="achievements-dialog"
      title={
        <span data-testid="stars-total">
          {t('achievements.mine')} <span className="gold-star">★</span>
          {t('achievements.count', { stars, total: ACHIEVEMENT_STARS })}
        </span>
      }
      testId="achievements"
    >
      <div className="show-first">
        {t('achievements.show_first')}
        <span className="segmented">
          {(['completed', 'locked'] as const).map((f) => (
            <button key={f} className={first === f ? 'on' : ''} onClick={() => setFirst(f)}>
              {t(`achievements.${f}`)}
            </button>
          ))}
        </span>
      </div>
      {!items ? (
        <Spinner />
      ) : (
        <div className="medal-grid">
          {shown.map((a) => (
            <button key={a.code} onClick={() => setOpen(a)} data-testid={`achievement-${a.code}`}>
              <Medal a={a} />
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="card-layer" onClick={(e) => e.target === e.currentTarget && setOpen(null)}>
          <div className="white-card" role="dialog" data-testid="achievement-detail">
            <button className="dialog-close" onClick={() => setOpen(null)} aria-label={t('common.close')}>
              <CloseIcon />
            </button>
            <div className="dialog-title">{t('achievements.detail')}</div>
            <p className="center-text">{t('achievements.meet')}</p>
            <div className="medal-big">
              <Medal a={open} size="large" />
            </div>
            <b className="center-text">{t(`achievement.${open.code}.title`)}</b>
            <div className="condition">
              <span className={open.stars >= open.levels.length ? 'check done' : 'check'}>✓</span>
              <span>
                {t(`achievement.${open.code}.desc`, { count: goal })}
                <small>
                  {open.progress} / {goal}
                  {open.nextReward ? ` · +❤${open.nextReward}` : ''}
                </small>
              </span>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}
